/**
 * Look before you move (My week, 3.2): what a click on a visit card tells the
 * clinician before she picks it up — the prior visit of her discipline and the
 * gap to this one, the plan of care, where the 30-day period ends, and every
 * discipline's visits in the start-of-care week this visit belongs to.
 *
 * Pure, and read-only: looking writes nothing. The patient's visits come from
 * the plan the patient calendar draws (every clinician, placeholders included),
 * each at the day it actually sits on — a visit moved on My week moves the real
 * visit and leaves the plan visit's own date behind (`effectivePlacement`).
 *
 * A patient with no plan has no plan of care, no period and no start-of-care
 * week to show (MY-WEEK-INSPECT-PROMPT.md, "Visits with no plan are out"). The
 * popover then carries the header and the prior visit, read from the tenant's
 * own visits for that patient — which reach only as far back as the horizon, so
 * finding none there says `none in view` rather than claiming there is none.
 */
import type { DateKey, DemoState, DisciplineGroup, Episode, PlanVisit, VisitResourceLike } from "@caredna-marlin/utils/homeHealthDemo/state";
import { addDaysToKey, daysBetweenKeys, isoWeekday, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { effectivePlacement, evaluateConstraints } from "./constraints";
import { effectiveVisitResources } from "./effectiveCalendar";
import { isRoutine } from "./moveGuard";
import { disciplineForVisitTypeKind, selectPlanProgress } from "./planProgress";
import { disciplineGroupFor } from "./roster";

/** Which card was clicked: a real visit, or a plan placeholder with no real visit behind it. */
export type InspectTarget = { realVisitId: number } | { planVisitId: string };

export type PillState = "done" | "scheduled" | "reserved";

export interface InspectPill {
  key: string;
  date: DateKey;
  /** SOC, E (evaluation), R (recertification), ROC — or empty for a routine visit. */
  prefix: string;
  state: PillState;
  selected: boolean;
  /**
   * The patient is not available on this day — said only of a visit still to
   * come. A visit already done is done; flagging it reads as if it had not been.
   */
  unavailable: boolean;
}

export interface InspectWeekRow {
  group: DisciplineGroup;
  pills: InspectPill[];
}

export interface VisitInspection {
  patientRef?: string;
  /** The tenant's patient id, for the link to the patient page. */
  patientId?: number;
  /** The selected visit's discipline group: the clinician's discipline. */
  group?: DisciplineGroup;
  date: DateKey;
  prior:
    | { kind: "found"; group: DisciplineGroup; date: DateKey; gapDays: number; state: "done" | "scheduled" }
    | { kind: "noneYet"; group?: DisciplineGroup }
    | { kind: "noneInView"; group?: DisciplineGroup };
  /** Absent for a patient with no plan. */
  plan?: {
    groups: { group: DisciplineGroup; delivered: number; ordered: number; pending: boolean; behind: boolean }[];
    dayOfCert: number;
    certPeriodDays: number;
  };
  /** The end of the 30-day period today is in; absent for a patient with no plan. */
  periodEnd?: { date: DateKey; soon?: "this week" | "next week" };
  /**
   * The start-of-care week the selected visit falls in; absent for a patient with
   * no plan. `ordered` is the clinician's discipline's routine visits the plan has
   * in that week — the number the guard's "Week N drops to …" counts from — and
   * is absent while that discipline's orders are still to follow.
   */
  week?: { index: number; start: DateKey; end: DateKey; rows: InspectWeekRow[]; ordered?: number };
}

const GROUP_ORDER: DisciplineGroup[] = ["SN", "PT", "OT", "SLP", "MSW", "HHA"];
const GONE = new Set<PlanVisit["status"]>(["canceled", "missed"]);
const ORDERED = new Set<PlanVisit["status"]>(["placeholder", "real", "completed", "missed"]);

/** The prefix the patient calendar marks a visit with. */
export function pillPrefix(visit: Pick<PlanVisit, "visitTypeKind"> & Partial<Pick<PlanVisit, "rule">>): string {
  if (visit.rule === "recert" || visit.visitTypeKind === "RECERT") return "R";
  if (visit.visitTypeKind === "ROC") return "ROC";
  if (visit.visitTypeKind === "SOC") return "SOC";
  if (visit.visitTypeKind.endsWith("_EVAL")) return "E";
  return "";
}

/** Sunday-to-Saturday weeks, as My week draws them: is `date` in today's week, or the one after? */
function weekLabelFor(date: DateKey, today: DateKey): "this week" | "next week" | undefined {
  const sunday = addDaysToKey(today, -(isoWeekday(today) % 7));
  if (date < sunday) return undefined;
  if (date <= addDaysToKey(sunday, 6)) return "this week";
  if (date <= addDaysToKey(sunday, 13)) return "next week";
  return undefined;
}

function patientRefForId(state: Pick<DemoState, "meta">, patientId: number): string | undefined {
  return Object.entries(state.meta.bindings.patientIdsByRef).find(([, id]) => id === patientId)?.[0];
}

function latestEpisodeFor(state: Pick<DemoState, "episodes">, patientRef: string): Episode | undefined {
  return Object.values(state.episodes)
    .filter((e) => e.patientRef === patientRef)
    .sort((a, b) => b.socDate.localeCompare(a.socDate))[0];
}

function kindOfResource(state: Pick<DemoState, "meta">, vr: VisitResourceLike): PlanVisit["visitTypeKind"] | undefined {
  return Object.entries(state.meta.bindings.visitTypeIdsByKind).find(([, id]) => id === vr.visitTypeId)?.[0] as PlanVisit["visitTypeKind"] | undefined;
}

function groupOfResource(state: Pick<DemoState, "meta" | "config">, vr: VisitResourceLike): DisciplineGroup | undefined {
  const kind = kindOfResource(state, vr);
  const discipline = kind ? disciplineForVisitTypeKind(state.config, kind) : undefined;
  return discipline ? disciplineGroupFor(discipline) : undefined;
}

/** The plan line and Period end, from the plan the patient page reads. */
function planLines(state: DemoState, episode: Episode, today: DateKey): Pick<VisitInspection, "plan" | "periodEnd"> {
  const progress = selectPlanProgress(state, episode.patientRef, today);
  const plan = progress && {
    groups: progress.groups.map((g) => {
      const dueByToday = Object.values(state.planVisits).filter(
        (v) => v.episodeId === episode.id && ORDERED.has(v.status) && disciplineGroupFor(v.discipline) === g.group && v.targetDate < today && v.targetDate >= episode.socDate,
      ).length;
      return { group: g.group, delivered: g.delivered, ordered: g.ordered, pending: g.pending, behind: !g.pending && g.delivered < dueByToday };
    }),
    dayOfCert: progress.dayOfCert,
    certPeriodDays: progress.certPeriodDays,
  };
  const current = progress?.periods.find((p) => today >= p.start && today <= p.end) ?? progress?.periods[progress.periods.length - 1];
  const periodEnd = current && {
    date: current.end,
    soon: daysBetweenKeys(today, current.end) < 14 ? weekLabelFor(current.end, today) : undefined,
  };
  return { plan, periodEnd };
}

/**
 * What the plan orders of one discipline group in one start-of-care week (Pete,
 * 2026-10-08): routine visits only — a start of care, an evaluation or a rule
 * visit is an order of its own — each counted on the day it sits on, exactly as
 * the move guard counts them. Absent while the group's orders are to follow.
 */
function orderedInWeek(state: DemoState, episode: Episode, group: DisciplineGroup | undefined, window: { start: DateKey; end: DateKey }, plan: VisitInspection["plan"]): number | undefined {
  if (!group || plan?.groups.find((g) => g.group === group)?.pending) return undefined;
  return Object.values(state.planVisits).filter((v) => {
    if (v.episodeId !== episode.id || GONE.has(v.status) || disciplineGroupFor(v.discipline) !== group || !isRoutine(state, v)) return false;
    const day = effectivePlacement(state, v).targetDate;
    return day >= window.start && day <= window.end;
  }).length;
}

/** The start-of-care week a day falls in. */
function socWeekOf(episode: Episode, date: DateKey): { start: DateKey; end: DateKey } {
  const index = Math.floor(daysBetweenKeys(episode.socDate, date) / 7);
  const start = addDaysToKey(episode.socDate, index * 7);
  return { start, end: addDaysToKey(start, 6) };
}

export function selectVisitInspection(state: DemoState, target: InspectTarget, wallNow: Date = new Date()): VisitInspection | undefined {
  const tz = state.config.branch.timeZone;
  const today = toDateKey(selectDemoNow(state, wallNow), tz);
  const resources = effectiveVisitResources(state);

  // --- what was clicked --------------------------------------------------------
  let planVisit: PlanVisit | undefined;
  let resource: VisitResourceLike | undefined;
  if ("realVisitId" in target) {
    resource = resources.find((v) => v.visitId === target.realVisitId);
    planVisit = Object.values(state.planVisits).find((v) => v.realVisitId === target.realVisitId);
    if (!resource && !planVisit) return undefined;
  } else {
    planVisit = state.planVisits[target.planVisitId];
    if (!planVisit) return undefined;
  }
  const episode = planVisit ? state.episodes[planVisit.episodeId] : undefined;
  const patientRef = episode?.patientRef ?? (resource ? patientRefForId(state, resource.patientId) : undefined);
  const patientId = resource?.patientId ?? (patientRef ? state.meta.bindings.patientIdsByRef[patientRef] : undefined);
  const date = resource ? toDateKey(new Date(resource.startIso), tz) : effectivePlacement(state, planVisit!).targetDate;
  const group = planVisit ? disciplineGroupFor(planVisit.discipline) : resource ? groupOfResource(state, resource) : undefined;

  // --- a real visit the plan has not linked: header and prior visit, from the tenant
  if (!planVisit || !episode) {
    const earlier = resources
      .filter((v) => v.patientId === resource!.patientId && v.visitId !== resource!.visitId && v.eventStatus !== "canceled")
      .map((v) => ({ v, day: toDateKey(new Date(v.startIso), tz) }))
      .filter(({ v, day }) => day < date && groupOfResource(state, v) === group)
      .sort((a, b) => b.day.localeCompare(a.day))[0];
    const prior: VisitInspection["prior"] = earlier
      ? { kind: "found", group: group!, date: earlier.day, gapDays: daysBetweenKeys(earlier.day, date), state: earlier.v.eventStatus === "complete" ? "done" : "scheduled" }
      : { kind: "noneInView", group };

    // **The patient has a plan even if this visit is not linked to it** (Pete,
    // 2026-10-08). On a seed whose weekday shifts the plan's days — Whitfield's
    // nursing lands on Saturdays in the plan and on Monday and Tuesday in the
    // tenant on a Friday seed — the visit binds to nothing, but the episode is
    // still his. So the plan line and Period end come from the episode, and the
    // week is drawn from the days his visits actually sit on.
    const looseEpisode = patientRef ? latestEpisodeFor(state, patientRef) : undefined;
    if (!looseEpisode || !resource) return { patientRef, patientId, group, date, prior };
    const patient = state.patients[looseEpisode.patientRef];
    const window = socWeekOf(looseEpisode, date);
    const rowsByGroup = new Map<DisciplineGroup, InspectPill[]>();
    const add = (g: DisciplineGroup, pill: InspectPill) => rowsByGroup.set(g, [...(rowsByGroup.get(g) ?? []), pill]);
    const unavailableOn = (day: DateKey) => !!patient && evaluateConstraints(patient, { targetDate: day }).some((h) => h.constraint.kind === "availabilityDays");
    for (const v of resources) {
      if (v.patientId !== resource.patientId || v.eventStatus === "canceled") continue;
      const day = toDateKey(new Date(v.startIso), tz);
      const kind = kindOfResource(state, v);
      const g = groupOfResource(state, v);
      if (day < window.start || day > window.end || !kind || !g) continue;
      const pillState: PillState = v.eventStatus === "complete" || day < today ? "done" : "scheduled";
      add(g, { key: `visit-${v.visitId}`, date: day, prefix: pillPrefix({ visitTypeKind: kind }), state: pillState, selected: v.visitId === resource.visitId, unavailable: pillState !== "done" && unavailableOn(day) });
    }
    // The plan's own reservations in that week still belong on it.
    for (const v of Object.values(state.planVisits)) {
      if (v.episodeId !== looseEpisode.id || v.status !== "placeholder") continue;
      const day = effectivePlacement(state, v).targetDate;
      if (day < window.start || day > window.end) continue;
      add(disciplineGroupFor(v.discipline), { key: v.id, date: day, prefix: pillPrefix(v), state: "reserved", selected: false, unavailable: unavailableOn(day) });
    }
    const rows = GROUP_ORDER.filter((g) => rowsByGroup.has(g)).map((g) => ({ group: g, pills: rowsByGroup.get(g)!.sort((a, b) => a.date.localeCompare(b.date)) }));
    const lines = planLines(state, looseEpisode, today);
    return {
      patientRef: looseEpisode.patientRef,
      patientId,
      group,
      date,
      prior,
      ...lines,
      week: {
        index: Math.floor(daysBetweenKeys(looseEpisode.socDate, window.start) / 7) + 1,
        start: window.start,
        end: window.end,
        rows,
        ordered: orderedInWeek(state, looseEpisode, group, window, lines.plan),
      },
    };
  }

  // --- a patient with a plan: everything from the plan the calendar draws -------
  const patient = state.patients[episode.patientRef];
  const mine = Object.values(state.planVisits)
    .filter((v) => v.episodeId === episode.id && !GONE.has(v.status))
    .map((v) => ({ v, day: v.id === planVisit!.id ? date : effectivePlacement(state, v).targetDate }));

  const before = mine
    .filter(({ v, day }) => v.id !== planVisit!.id && disciplineGroupFor(v.discipline) === group && (v.status === "completed" || v.status === "real") && day < date)
    .sort((a, b) => b.day.localeCompare(a.day))[0];
  const prior: VisitInspection["prior"] = before
    ? { kind: "found", group: group!, date: before.day, gapDays: daysBetweenKeys(before.day, date), state: before.v.status === "completed" || before.day < today ? "done" : "scheduled" }
    : { kind: "noneYet", group };

  const { plan, periodEnd } = planLines(state, episode, today);

  // The start-of-care week the visit **sits in**. Not the plan visit's own
  // compliance week: a visit pinned to a working day keeps the week number the
  // plan gave it, so on a Friday seed Osei's Monday drew "Week 5 · Wed Oct 14 –
  // Tue Oct 20", a week the card is not in (Pete, 2026-10-08). For a visit inside
  // its own week the two are the same.
  const window = socWeekOf(episode, date);
  const rowsByGroup = new Map<DisciplineGroup, InspectPill[]>();
  for (const { v, day } of mine) {
    if (day < window.start || day > window.end) continue;
    const g = disciplineGroupFor(v.discipline);
    // As the patient calendar colours a chip: behind us is done, a real visit
    // still to come is scheduled, anything else is a reservation.
    const pillState: PillState = v.status === "completed" || day < today ? "done" : v.status === "real" ? "scheduled" : "reserved";
    const pill: InspectPill = {
      key: v.id,
      date: day,
      prefix: pillPrefix(v),
      state: pillState,
      selected: v.id === planVisit.id,
      unavailable: pillState !== "done" && !!patient && evaluateConstraints(patient, { targetDate: day }).some((h) => h.constraint.kind === "availabilityDays"),
    };
    rowsByGroup.set(g, [...(rowsByGroup.get(g) ?? []), pill]);
  }
  const rows = GROUP_ORDER.filter((g) => rowsByGroup.has(g)).map((g) => ({ group: g, pills: rowsByGroup.get(g)!.sort((a, b) => a.date.localeCompare(b.date)) }));

  return {
    patientRef: episode.patientRef,
    patientId,
    group,
    date,
    prior,
    plan,
    periodEnd,
    week: { index: Math.floor(daysBetweenKeys(episode.socDate, window.start) / 7) + 1, start: window.start, end: window.end, rows, ordered: orderedInWeek(state, episode, group, window, plan) },
  };
}
